import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ButtonGroup, makeStyles, Typography } from '@material-ui/core';

import { FieldType } from '..';
import { FieldBooleanResponse } from '../../../.generated/admin-api';
import { InputValidationResult } from '../..';
import { apiTranslate } from '../../../translations/translations';

type BooleanFieldInputProps = {
  field: FieldType;
  required: boolean;
  value?: boolean;
  disabled?: boolean;
  onChangeValidator: (value?: boolean) => InputValidationResult;
};

const useStyles = makeStyles((theme) => ({
  label: {
    paddingBottom: theme.spacing(2),
  },
  error: {
    paddingTop: theme.spacing(2),
    color: theme.palette.error.main,
    fontSize: '0.8rem',
  },
}));

const BooleanField: React.FC<BooleanFieldInputProps> = (props) => {
  const { field, value, disabled, onChangeValidator } = props;

  const { t } = useTranslation(['translations']);

  const classes = useStyles();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const fieldBoolean = field as FieldBooleanResponse;

  const setValue = (value?: boolean) => {
    const validationResult = onChangeValidator(value);

    if (validationResult) {
      if (!validationResult.isValid) {
        const errorMessage =
          validationResult.errorMessage ??
          (validationResult.errorMessageKey
            ? t(
                validationResult.errorMessageKey,
                validationResult.errorMessageOptions,
              )
            : t('validation.default'));

        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(undefined);
      }
    }
  };

  const trueButtonClicked = () => {
    if (disabled !== true) {
      setValue(value ? undefined : true);
    }
  };

  const falseButtonClicked = () => {
    if (disabled !== true) {
      setValue(value !== undefined && !value ? undefined : false);
    }
  };

  const title = apiTranslate(fieldBoolean.title);
  const yes = fieldBoolean.trueLabel
    ? apiTranslate(fieldBoolean.trueLabel)
    : 'Yes';
  const no = fieldBoolean.falseLabel
    ? apiTranslate(fieldBoolean.falseLabel)
    : 'No';

  return (
    <>
      <Typography className={classes.label}>{title}</Typography>
      <ButtonGroup
        id="button-group"
        disableElevation
        aria-label="outlined primary button group"
        fullWidth
      >
        <Button
          color={value !== undefined && value ? 'primary' : 'secondary'}
          onClick={trueButtonClicked}
        >
          {yes}
        </Button>
        <Button
          color={value !== undefined && !value ? 'primary' : 'secondary'}
          onClick={falseButtonClicked}
        >
          {no}
        </Button>
      </ButtonGroup>
      <Typography className={classes.error}>
        {!!errorMessage && errorMessage}
      </Typography>
    </>
  );
};

export { BooleanField };
